import React, { useState, useRef } from "react";
import { Mic, Square, Loader2, Type, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";

// Voice + AI form filler. Records audio -> Whisper -> GPT -> structured fields.
export const VoiceInput = ({ formType, onResult, label = "Preencher com voz" }) => {
    const [recording, setRecording] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [typedOpen, setTypedOpen] = useState(false);
    const [typedText, setTypedText] = useState("");
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream);
            chunksRef.current = [];
            mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(chunksRef.current, { type: "audio/webm" });
                await sendAudio(blob);
            };
            mr.start();
            mediaRecorderRef.current = mr;
            setRecording(true);
        } catch (e) {
            toast.error("Não foi possível acessar o microfone. Verifique as permissões.");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    const sendAudio = async (blob) => {
        setProcessing(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "audio.webm");
            fd.append("form_type", formType);
            const { data } = await api.post("/ai/voice-fill", fd, { headers: { "Content-Type": "multipart/form-data" } });
            onResult(data.fields || {}, data.transcript || "");
            toast.success("Campos preenchidos por voz!");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao processar áudio");
        } finally {
            setProcessing(false);
        }
    };

    const sendTyped = async () => {
        if (!typedText.trim()) return;
        setProcessing(true);
        try {
            const { data } = await api.post("/ai/parse-text", { text: typedText, form_type: formType });
            onResult(data.fields || {}, data.transcript || "");
            setTypedOpen(false);
            setTypedText("");
            toast.success("Campos preenchidos pela IA!");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao interpretar texto");
        } finally {
            setProcessing(false);
        }
    };

    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid={`voice-input-${formType}`}>
            <button
                type="button"
                onClick={recording ? stopRecording : startRecording}
                disabled={processing}
                data-testid={`voice-record-btn-${formType}`}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60 ${recording ? "bg-expense mic-recording" : "bg-accent"}`}
            >
                {processing ? <Loader2 className="h-4 w-4 animate-spin" /> : recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                {processing ? "Processando..." : recording ? "Parar gravação" : label}
            </button>

            <Popover open={typedOpen} onOpenChange={setTypedOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid={`voice-type-btn-${formType}`}
                        className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
                    >
                        <Type className="h-4 w-4" /> Ditar por texto
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-80 rounded-2xl" align="start">
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm font-semibold text-primary">
                            <Sparkles className="h-4 w-4 text-accent" /> Descreva e a IA preenche
                        </div>
                        <Textarea
                            data-testid={`voice-type-textarea-${formType}`}
                            value={typedText}
                            onChange={(e) => setTypedText(e.target.value)}
                            placeholder={formType === "expense" ? "Ex: Gastei 150 reais no mercado hoje" : "Ex: Levei a criança ao parque, almoço às 12h, tudo tranquilo"}
                            rows={4}
                            className="rounded-xl"
                        />
                        <Button onClick={sendTyped} disabled={processing} className="w-full rounded-full" data-testid={`voice-type-submit-${formType}`}>
                            {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Sparkles className="h-4 w-4 mr-2" />}
                            Preencher
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};
